// src/pages/EditInstallationAudit.tsx
import React, { useState, useEffect, FormEvent } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";

interface InstallationAuditData {
  id?: number;
  growerName: string;
  installationLocation: string;
  guidelinesFollowed: string;
  issuesFound: string;
}

interface Report {
  id: number;
  created_at: string;
}

const EditInstallationAudit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [rows, setRows] = useState<InstallationAuditData[]>([]);
  const [deletedIds, setDeletedIds] = useState<number[]>([]);
  const [newestReportId, setNewestReportId] = useState<number | null>(null);
  const [error, setError] = useState("");

  // Fetch or create report
  useEffect(() => {
    fetch(`/api/dealers/${id}/reports`)
      .then((res) => res.json())
      .then((data: Report[]) => {
        if (data.length) setNewestReportId(data[0].id);
        else
          fetch(`/api/dealers/${id}/reports`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
          })
            .then((r) => r.json())
            .then((nr) => setNewestReportId(nr.id))
            .catch(() => setError("Unable to create report"));
      })
      .catch(() => setError("Unable to fetch reports"));
  }, [id]);

  // Load existing rows
  useEffect(() => {
    if (!newestReportId) return;
    fetch(`/api/reports/${newestReportId}/installation-audit`)
      .then((res) => res.json())
      .then((data: any[]) => {
        if (Array.isArray(data) && data.length) {
          setRows(
            data.map((rec) => ({
              id: rec.id,
              growerName: rec.grower_name || "",
              installationLocation: rec.installation_location || "",
              guidelinesFollowed: rec.guidelines_followed ? "Yes" : "No",
              issuesFound: rec.issues_found || "",
            }))
          );
        } else {
          setRows([{
            growerName: "",
            installationLocation: "",
            guidelinesFollowed: "",
            issuesFound: ""
          }]);
        }
      })
      .catch(() => setRows([]));
  }, [newestReportId]);

  const handleChange = (idx: number, field: keyof InstallationAuditData, val: string) => {
    setRows((prev) => {
      const copy = [...prev];
      (copy[idx] as any)[field] = val;
      return copy;
    });
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      { growerName: "", installationLocation: "", guidelinesFollowed: "", issuesFound: "" }
    ]);
  };

  const deleteRow = (idx: number) => {
    setRows((prev) => {
      const rec = prev[idx];
      if (rec.id) setDeletedIds((ids) => [...ids, rec.id!]);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!newestReportId) {
      setError("No report found");
      return;
    }

    // DELETE removed rows (ignore 404)
    if (deletedIds.length) {
      await Promise.all(
        deletedIds.map(async (rid) => {
          try {
            const resp = await fetch(
              `/api/reports/${newestReportId}/installation-audit/${rid}`,
              { method: "DELETE" }
            );
            if (!resp.ok && resp.status !== 404) {
              const err = await resp.json().catch(() => ({}));
              throw new Error(err.error || `Delete failed for ${rid}`);
            }
          } catch (err) {
            console.error("Delete error id", rid, err);
          }
        })
      );
    }

    // Split into updates vs creates
    const updates = rows.filter((r) => r.id != null);
    const creates = rows.filter((r) => r.id == null);

    try {
      // Updates
      for (const r of updates) {
        const res = await fetch(
          `/api/reports/${newestReportId}/installation-audit`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id: r.id,
              grower_name: r.growerName,
              installation_location: r.installationLocation,
              guidelines_followed: r.guidelinesFollowed.toLowerCase() === "yes",
              issues_found: r.issuesFound
            }),
          }
        );
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || `Update failed for ${r.id}`);
        }
      }

      // Creates
      for (const r of creates) {
        const res = await fetch(
          `/api/reports/${newestReportId}/installation-audit`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              dealer_id: parseInt(id!, 10),
              grower_name: r.growerName,
              installation_location: r.installationLocation,
              guidelines_followed: r.guidelinesFollowed.toLowerCase() === "yes",
              issues_found: r.issuesFound
            }),
          }
        );
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || "Create failed");
        }
      }

      navigate(`/dealer/${id}`);
    } catch (err: any) {
      console.error("Save failed:", err);
      setError(err.message || "Unknown error");
    }
  };

  // Styles unchanged
  const containerStyle: React.CSSProperties = { backgroundColor: "#F0F5F0", minHeight: "100vh", padding: "20px" };
  const cardStyle: React.CSSProperties = { backgroundColor: "#FFFFFF", borderRadius: "8px", boxShadow: "0 2px 5px rgba(0,0,0,0.1)", maxWidth: "800px", margin: "30px auto", padding: "30px" };
  const headerStyle: React.CSSProperties = { textAlign: "center", color: "#e41e26", borderBottom: "2px solid #e41e26", paddingBottom: "10px", marginBottom: "20px", fontSize: "24px" };
  const labelStyle: React.CSSProperties = { display: "block", marginBottom: "5px", fontWeight: "bold" };
  const inputStyle: React.CSSProperties = { width: "100%", padding: "8px", marginBottom: "10px", borderRadius: "4px", border: "1px solid #ccc" };
  const buttonStyle: React.CSSProperties = { padding: "5px 10px", backgroundColor: "#e41e26", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", marginRight: "10px" };
  const deleteButtonStyle: React.CSSProperties = { ...buttonStyle, backgroundColor: "transparent", color: "#e41e26", fontSize: "18px", padding: "0 8px" };

  return (
    <div style={containerStyle}>
      <Navbar />
      <div style={cardStyle}>
        <h1 style={headerStyle}>Edit Installation Audit</h1>
        {error && <p style={{ color: "red", textAlign: "center" }}>{error}</p>}
        <form onSubmit={handleSubmit}>
          {rows.map((r, idx) => (
            <div
              key={r.id ? `${r.id}-${idx}` : `new-${idx}`}
              style={{ marginBottom: "15px", border: "1px solid #ccc", padding: "10px", borderRadius: "4px", position: "relative" }}
            >
              <button
                type="button"
                onClick={() => deleteRow(idx)}
                style={{ ...deleteButtonStyle, position: "absolute", top: "10px", right: "10px" }}
                title="Delete this row"
              >
                🗑️
              </button>

              <label style={labelStyle}>Grower Name:</label>
              <input
                name="growerName"
                value={r.growerName}
                onChange={(e) => handleChange(idx, "growerName", e.target.value)}
                style={inputStyle}
              />

              <label style={labelStyle}>Installation Location:</label>
              <input
                name="installationLocation"
                value={r.installationLocation}
                onChange={(e) => handleChange(idx, "installationLocation", e.target.value)}
                style={inputStyle}
              />

              <label style={labelStyle}>Guidelines Followed:</label>
			<select
			  name="guidelinesFollowed"
			  value={r.guidelinesFollowed}
			  onChange={(e) => handleChange(idx, "guidelinesFollowed", e.target.value)}
			  style={inputStyle}
			>
			  <option value="">Select</option>
			  <option value="Yes">Yes</option>
			  <option value="No">No</option>
			</select>

              <label style={labelStyle}>Issues Found:</label>
              <input
                name="issuesFound"
                value={r.issuesFound}
                onChange={(e) => handleChange(idx, "issuesFound", e.target.value)}
                style={inputStyle}
              />
            </div>
          ))}

          <button type="button" onClick={addRow} style={buttonStyle}>Add Row</button>
          <br /><br />
          <button type="submit" style={buttonStyle}>Save</button>
          <button type="button" style={buttonStyle} onClick={() => navigate(`/dealer/${id}`)}>Cancel</button>
        </form>
      </div>
    </div>
  );
};

export default EditInstallationAudit;
